/**
 * The PRISMA flow diagram generator's form: reading its boxes into `PrismaInput`, the
 * sentences that explain a failed check, and the table of every box and its number.
 * Words arrive as labels from the widget's strings.
 */

import { t } from "@/lib/i18n/interpolate";
import { parseNumberInput } from "@/lib/parse-number";
import type { NumberFormat } from "@/lib/tool-kit/charts/format";
import {
  type CalculatedField,
  type PrismaCheck,
  type PrismaInput,
  type PrismaValues,
  parseSourceList,
} from "@/lib/tool-kit/diagrams/prisma";

export type PrismaCountKey =
  | "databases"
  | "registers"
  | "duplicates"
  | "automation"
  | "otherRemoved"
  | "screened"
  | "excluded"
  | "sought"
  | "notRetrieved"
  | "assessed"
  | "studies"
  | "reports"
  | "otherSought"
  | "otherNotRetrieved"
  | "otherAssessed";

export type PrismaListKey =
  | "databaseList"
  | "registerList"
  | "reasons"
  | "otherSources"
  | "otherReasons";

/**
 * The example review's counts: illustrative, chosen to add up. The steps left blank —
 * the totals, screened, sought, assessed, reports — are worked out from the rest.
 */
export const PRISMA_EXAMPLE_COUNTS: Record<PrismaCountKey, string> = {
  databases: "",
  registers: "",
  duplicates: "612",
  automation: "87",
  otherRemoved: "0",
  screened: "",
  excluded: "1498",
  sought: "",
  notRetrieved: "6",
  assessed: "",
  studies: "19",
  reports: "",
  otherSought: "12",
  otherNotRetrieved: "2",
  otherAssessed: "",
};

/** The worked-out value a blank field shows as its placeholder. */
export const PRISMA_WORKED_OUT: Partial<
  Record<PrismaCountKey, CalculatedField>
> = {
  databases: "databases",
  registers: "registers",
  screened: "screened",
  sought: "sought",
  assessed: "assessed",
  reports: "reports",
  otherAssessed: "otherAssessed",
};

/** A typed count, rounded to a whole number — records are not fractional. */
function count(raw: string): number | null {
  const value = parseNumberInput(raw);
  return value === null ? null : Math.round(value);
}

export function readPrismaForm(
  counts: Record<PrismaCountKey, string>,
  lists: Record<PrismaListKey, string>,
  other: boolean
): PrismaInput {
  return {
    databases: count(counts.databases),
    databaseList: parseSourceList(lists.databaseList),
    registers: count(counts.registers),
    registerList: parseSourceList(lists.registerList),
    duplicates: count(counts.duplicates),
    automation: count(counts.automation),
    otherRemoved: count(counts.otherRemoved),
    screened: count(counts.screened),
    excluded: count(counts.excluded),
    sought: count(counts.sought),
    notRetrieved: count(counts.notRetrieved),
    assessed: count(counts.assessed),
    reasons: parseSourceList(lists.reasons),
    studies: count(counts.studies),
    reports: count(counts.reports),
    other: other
      ? {
          sources: parseSourceList(lists.otherSources),
          sought: count(counts.otherSought),
          notRetrieved: count(counts.otherNotRetrieved),
          assessed: count(counts.otherAssessed),
          reasons: parseSourceList(lists.otherReasons),
        }
      : null,
  };
}

export interface PrismaCheckLabels {
  databaseTotal: string;
  registerTotal: string;
  mismatchScreened: string;
  mismatchSought: string;
  mismatchAssessed: string;
  mismatchOtherAssessed: string;
  mismatchReports: string;
  negative: string;
  studiesOverReports: string;
  otherSoughtOverIdentified: string;
  reasonsOverAssessed: string;
  /** Box names, for "{box} comes out at …". */
  screened: string;
  sought: string;
  assessed: string;
  reports: string;
}

type StepField =
  | "screened"
  | "sought"
  | "assessed"
  | "reports"
  | "otherAssessed";

const MISMATCH: Record<StepField, keyof PrismaCheckLabels> = {
  screened: "mismatchScreened",
  sought: "mismatchSought",
  assessed: "mismatchAssessed",
  reports: "mismatchReports",
  otherAssessed: "mismatchOtherAssessed",
};

const BOX_NAMES: Record<StepField, keyof PrismaCheckLabels> = {
  screened: "screened",
  sought: "sought",
  assessed: "assessed",
  reports: "reports",
  otherAssessed: "assessed",
};

/** One sentence saying what does not add up and what it should be. */
export function prismaCheckText(
  check: PrismaCheck,
  labels: PrismaCheckLabels,
  fmt: NumberFormat
): string {
  switch (check.kind) {
    case "listTotal":
      return t(
        check.field === "databases"
          ? labels.databaseTotal
          : labels.registerTotal,
        { sum: fmt(check.sum), entered: fmt(check.entered) }
      );
    case "mismatch":
      return t(labels[MISMATCH[check.field]], {
        expected: fmt(check.expected),
        entered: fmt(check.entered),
      });
    case "negative":
      return t(labels.negative, {
        box: labels[BOX_NAMES[check.field]],
        value: fmt(check.value),
      });
    case "studiesOverReports":
      return t(labels.studiesOverReports, {
        studies: fmt(check.studies),
        reports: fmt(check.reports),
      });
    case "otherSoughtOverIdentified":
      return t(labels.otherSoughtOverIdentified, {
        sought: fmt(check.sought),
        identified: fmt(check.identified),
      });
    default:
      return t(labels.reasonsOverAssessed, {
        reasons: fmt(check.reasons),
        assessed: fmt(check.assessed),
      });
  }
}

export interface PrismaRowLabels {
  databases: string;
  registers: string;
  duplicates: string;
  automation: string;
  otherRemoved: string;
  screened: string;
  excluded: string;
  sought: string;
  notRetrieved: string;
  assessed: string;
  studies: string;
  reports: string;
  /** "Other methods: {box}" */
  otherPrefix: string;
}

/** Every box and its number, for the data table and "Copy counts". */
export function prismaCountRows(
  input: PrismaInput,
  values: PrismaValues,
  labels: PrismaRowLabels,
  fmt: NumberFormat
): string[][] {
  const n = (value: number | null) => (value === null ? "" : fmt(value));
  const sub = (label: string) => `— ${label}`;
  const rows: string[][] = [
    [labels.databases, n(values.databases)],
    ...input.databaseList.map((source) => [sub(source.label), n(source.n)]),
    [labels.registers, n(values.registers)],
    ...input.registerList.map((source) => [sub(source.label), n(source.n)]),
    [labels.duplicates, n(input.duplicates)],
    [labels.automation, n(input.automation)],
    [labels.otherRemoved, n(input.otherRemoved)],
    [labels.screened, n(values.screened)],
    [labels.excluded, n(input.excluded)],
    [labels.sought, n(values.sought)],
    [labels.notRetrieved, n(input.notRetrieved)],
    [labels.assessed, n(values.assessed)],
    ...input.reasons.map((reason) => [sub(reason.label), n(reason.n)]),
    [labels.studies, n(input.studies)],
    [labels.reports, n(values.reports)],
  ];
  const other = input.other;
  if (other) {
    const prefix = (box: string) => t(labels.otherPrefix, { box });
    rows.push(
      ...other.sources.map((source) => [prefix(source.label), n(source.n)]),
      [prefix(labels.sought), n(other.sought)],
      [prefix(labels.notRetrieved), n(other.notRetrieved)],
      [prefix(labels.assessed), n(values.otherAssessed)],
      ...other.reasons.map((reason) => [prefix(sub(reason.label)), n(reason.n)])
    );
  }
  return rows;
}
